/**
 * LiveChat Component
 * 
 * Real-time chat interface for manual intervention in WhatsApp conversations.
 * 
 * Features:
 * - Real-time message stream via polling (WebSocket ready)
 * - Send manual messages
 * - Pause/Resume AI toggle
 * - Full conversation takeover
 * - Message history with sender attribution
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import ContactCardMessage from './ContactCardMessage';
import { parseContactCardText } from '../utils/contactCardParser';
import { notify, confirmAsync } from '../utils/notify';
import './LiveChat.css';

function LiveChat() {
  const { t } = useTranslation();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);
  
  // State
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [aiPaused, setAiPaused] = useState(false);
  const [aiAutoResumeAt, setAiAutoResumeAt] = useState(null);
  const [canSendMessages, setCanSendMessages] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // 'pause', 'resume', 'takeover'
  
  // Helper: detect platform type
  const isInstagramDM = conversation?.platform?.toLowerCase().includes('instagram_dm');
  const isFacebookMessage = conversation?.platform?.toLowerCase().includes('facebook_message');
  const platformIcon = isFacebookMessage ? '📘' : isInstagramDM ? '📸' : '💬';
  const platformLabel = isFacebookMessage ? 'Facebook Messenger' : isInstagramDM ? 'Instagram DM' : 'WhatsApp';
  
  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);
  
  // Fetch conversation data
  const fetchConversation = useCallback(async (isPolling = false) => {
    try {
      if (!isPolling) setLoading(true);
      const response = await apiClient.get(`/api/v1/manual-chat/conversations/${conversationId}/live`);
      
      const data = response.data;
      setConversation(data);
      setMessages(data.messages || []);
      setAiPaused(data.ai_paused);
      setAiAutoResumeAt(data.ai_auto_resume_at);
      setCanSendMessages(data.can_send_messages);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch conversation:', err);
      if (!isPolling) {
        setError(err.response?.data?.detail || t('liveChat.errors.loadFailed'));
      }
    } finally {
      if (!isPolling) setLoading(false);
    }
  }, [conversationId, t]);
  
  // Initial fetch and polling setup
  useEffect(() => {
    fetchConversation();
    
    // Set up polling for real-time updates (every 3 seconds)
    pollingRef.current = setInterval(() => {
      fetchConversation(true);
    }, 3000);
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fetchConversation]);
  
  // Send manual message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !canSendMessages) return;
    
    setSending(true);
    try {
      const response = await apiClient.post(
        `/api/v1/manual-chat/conversations/${conversationId}/send`,
        {
          message: newMessage.trim(),
          auto_pause_ai: true
        }
      );
      
      if (response.data.success) {
        // Add message optimistically
        const newMsg = {
          id: Date.now(),
          direction: 'outbound',
          message_text: newMessage.trim(),
          sender_type: 'human_agent',
          created_at: new Date().toISOString()
        };
        setMessages(prev => [...prev, newMsg]);
        setNewMessage('');
        setAiPaused(response.data.ai_paused);
        
        // Scroll to bottom
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      notify.error(t('liveChat.errors.sendFailed', { error: err.response?.data?.detail || 'Unknown error' }));
    } finally {
      setSending(false);
    }
  };
  
  // Pause AI
  const handlePauseAI = async () => {
    setActionLoading('pause');
    try {
      const response = await apiClient.post(
        `/api/v1/manual-chat/conversations/${conversationId}/pause-ai`,
        { duration_minutes: 30, reason: 'Manual pause from live chat' }
      );
      
      if (response.data.success) {
        setAiPaused(true);
        setAiAutoResumeAt(response.data.auto_resume_at);
      }
    } catch (err) {
      console.error('Failed to pause AI:', err);
      notify.error(t('liveChat.errors.pauseFailed'));
    } finally {
      setActionLoading(null);
    }
  };
  
  // Resume AI
  const handleResumeAI = async () => {
    setActionLoading('resume');
    try {
      const response = await apiClient.post(
        `/api/v1/manual-chat/conversations/${conversationId}/resume-ai`
      );
      
      if (response.data.success) {
        setAiPaused(false);
        setAiAutoResumeAt(null);
      }
    } catch (err) {
      console.error('Failed to resume AI:', err);
      notify.error(t('liveChat.errors.resumeFailed'));
    } finally {
      setActionLoading(null);
    }
  };
  
  // Takeover conversation
  const handleTakeover = async () => {
    if (!(await confirmAsync({ message: t('liveChat.confirm.takeover') }))) return;
    
    setActionLoading('takeover');
    try {
      const response = await apiClient.post(
        `/api/v1/manual-chat/conversations/${conversationId}/takeover`,
        { reason: 'Manual takeover from live chat' }
      );
      
      if (response.data.success) {
        setAiPaused(true);
        setAiAutoResumeAt(null);
        notify.success(t('liveChat.success.takeover'));
      }
    } catch (err) {
      console.error('Failed to takeover:', err);
      notify.error(t('liveChat.errors.takeoverFailed'));
    } finally {
      setActionLoading(null);
    }
  };
  
  // Get sentiment color
  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return '#4CAF50';
      case 'negative': return '#F44336';
      case 'neutral': return '#FFC107';
      default: return '#9E9E9E';
    }
  };
  
  // Render message
  const renderMessage = (msg) => {
    const isInbound = msg.direction === 'inbound';
    const senderType = msg.sender_type || (isInbound ? 'user' : 'ai');

    let senderLabel;
    let senderIcon;

    switch (senderType) {
      case 'human_agent':
        senderLabel = t('liveChat.you');
        senderIcon = '👤';
        break;
      case 'ai':
        senderLabel = '🤖 AI';
        senderIcon = '🤖';
        break;
      case 'user':
      default:
        senderLabel = conversation?.contact_name || t('liveChat.contact');
        senderIcon = '💬';
        break;
    }

    // Contact-card branch. Prefer the structured ``extra_data`` payload
    // populated by the backend; fall back to the deterministic text
    // parser for legacy rows persisted before the migration.
    const extraPayload =
      msg.extra_data && msg.extra_data.kind === 'contact_card'
        ? msg.extra_data
        : null;
    const cardPayload =
      extraPayload || (msg.message_type === 'contact_card'
        ? parseContactCardText(msg.message_text)
        : parseContactCardText(msg.message_text));
    const isContactCard =
      msg.message_type === 'contact_card' || !!cardPayload;

    if (isContactCard && cardPayload) {
      return (
        <div
          key={msg.id}
          className={`live-message ${isInbound ? 'inbound' : 'outbound'} ${senderType} contact-card-wrapper`}
        >
          <div className="message-avatar">{senderIcon}</div>
          <ContactCardMessage
            card={cardPayload}
            direction={isInbound ? 'inbound' : 'outbound'}
            senderLabel={senderLabel}
            senderIcon={senderIcon}
            timestamp={msg.created_at}
          />
        </div>
      );
    }

    return (
      <div
        key={msg.id}
        className={`live-message ${isInbound ? 'inbound' : 'outbound'} ${senderType}`}
      >
        <div className="message-avatar">{senderIcon}</div>
        <div className="message-bubble">
          <div className="message-sender">{senderLabel}</div>
          <div className="message-content">{msg.message_text}</div>
          <div className="message-footer">
            <span className="message-time">
              {new Date(msg.created_at).toLocaleTimeString()}
            </span>
            {msg.sentiment && (
              <span
                className="message-sentiment"
                style={{ backgroundColor: getSentimentColor(msg.sentiment) }}
              >
                {msg.sentiment}
              </span>
            )}
            {msg.response_time_seconds && (
              <span className="message-response-time">
                ⏱ {msg.response_time_seconds}s
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // Format auto-resume time
  const formatAutoResume = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  if (loading) {
    return (
      <div className="live-chat loading">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="live-chat error">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <button className="btn-back" onClick={() => navigate(-1)}>
          ← {t('common.back_to_dashboard')}
        </button>
      </div>
    );
  }
  
  return (
    <div className="live-chat">
      {/* Header */}
      <div className="chat-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate(-1)}>
            ← {t('liveChat.back')}
          </button>
        </div>
        
        <div className="header-center">
          <div className="contact-info">
            <h2>{conversation?.contact_name || conversation?.contact_id}</h2>
            <div className="contact-meta">
              <span className={`platform-badge ${isInstagramDM ? 'instagram-dm' : ''}`}>
                {platformIcon} {platformLabel}
              </span>
              {conversation?.agent_type && (
                <span className="agent-badge">{conversation.agent_type}</span>
              )}
              {conversation?.overall_sentiment && (
                <span 
                  className="sentiment-badge"
                  style={{ backgroundColor: getSentimentColor(conversation.overall_sentiment) }}
                >
                  {conversation.overall_sentiment}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="header-right">
          <div className="ai-controls">
            {aiPaused ? (
              <>
                <div className="ai-status paused">
                  <span className="status-dot"></span>
                  <span className="status-text">{t('liveChat.aiPaused')}</span>
                  {aiAutoResumeAt && (
                    <span className="auto-resume">
                      {t('liveChat.autoResumeAt', { time: formatAutoResume(aiAutoResumeAt) })}
                    </span>
                  )}
                </div>
                <button 
                  className="btn-action btn-resume" 
                  onClick={handleResumeAI}
                  disabled={actionLoading === 'resume'}
                >
                  {actionLoading === 'resume' ? '...' : `▶️ ${t('liveChat.resumeAI')}`}
                </button>
              </>
            ) : (
              <>
                <div className="ai-status active">
                  <span className="status-dot"></span>
                  <span className="status-text">{t('liveChat.aiActive')}</span>
                </div>
                <button 
                  className="btn-action btn-pause" 
                  onClick={handlePauseAI}
                  disabled={actionLoading === 'pause'}
                >
                  {actionLoading === 'pause' ? '...' : `⏸️ ${t('liveChat.pauseAI')}`}
                </button>
                <button 
                  className="btn-action btn-takeover" 
                  onClick={handleTakeover}
                  disabled={actionLoading === 'takeover'}
                >
                  {actionLoading === 'takeover' ? '...' : `🎯 ${t('liveChat.takeover')}`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-label">{t('liveChat.totalMessages')}</span>
          <span className="stat-value">{conversation?.message_count || 0}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">{t('liveChat.sentimentScore')}</span>
          <span className="stat-value">
            {conversation?.average_sentiment_score?.toFixed(2) || 'N/A'}
          </span>
        </div>
      </div>
      
      {/* Messages */}
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="no-messages">
            <p>{t('liveChat.noMessages')}</p>
          </div>
        ) : (
          messages.map(renderMessage)
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Warning Banner when AI is active */}
      {!aiPaused && (
        <div className="warning-banner">
          <span className="warning-icon">⚠️</span>
          <span>{t('liveChat.aiActiveWarning')}</span>
        </div>
      )}
      
      {/* Input */}
      <form className="message-input-form" onSubmit={handleSendMessage}>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={
            canSendMessages 
              ? t('liveChat.placeholder') 
              : isFacebookMessage
                ? t('liveChat.noFacebookPage', 'No Facebook Page configured')
                : isInstagramDM
                  ? t('liveChat.noInstagramAccount', 'No Instagram account configured')
                  : t('liveChat.noWhatsAppNumber')
          }
          disabled={!canSendMessages || sending}
          className="message-input"
        />
        <button 
          type="submit" 
          disabled={!canSendMessages || !newMessage.trim() || sending}
          className="btn-send"
        >
          {sending ? (
            <span className="sending-spinner"></span>
          ) : (
            '📤'
          )}
        </button>
      </form>
    </div>
  );
}

export default LiveChat;
